import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "../../libs/utils";

const DEFAULT_PAGE_SIZE = 40;
const MENU_MAX_HEIGHT = 240;
const VIEWPORT_PADDING = 12;

const getOptionKey = (option) => String(option?.value ?? option?.label ?? "");

const stopMenuScrollPropagation = (event) => {
  event.stopPropagation();
};

const PaginatedSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Select option",
  disabled = false,
  className,
  buttonClassName,
  menuClassName,
  icon: Icon,
  pageSize = DEFAULT_PAGE_SIZE,
  portal = true,
  multiple = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);

  const selectedOption = useMemo(
    () => options.find((option) => String(option.value) === String(value)),
    [options, value]
  );
  const selectedValues = multiple && Array.isArray(value) ? value.map(String) : [];
  const selectedLabel = multiple
    ? selectedValues.map((id) => options.find((option) => String(option.value) === id)?.label || id).join(", ")
    : selectedOption?.label || (value ? String(value) : "");
  const visibleOptions = options.slice(0, visibleCount);

  const updateMenuPosition = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;

    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
    const spaceAbove = rect.top - VIEWPORT_PADDING;
    const openAbove =
      spaceBelow < Math.min(MENU_MAX_HEIGHT, spaceAbove) && spaceAbove > spaceBelow;
    const availableHeight = Math.max(
      120,
      Math.min(MENU_MAX_HEIGHT, (openAbove ? spaceAbove : spaceBelow) - gap)
    );
    const left = Math.max(
      VIEWPORT_PADDING,
      Math.min(rect.left, window.innerWidth - rect.width - VIEWPORT_PADDING)
    );
    const top = openAbove
      ? Math.max(VIEWPORT_PADDING, rect.top - gap - availableHeight)
      : rect.bottom + gap;

    setMenuStyle({
      left,
      top,
      width: rect.width,
      maxHeight: availableHeight,
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !rootRef.current?.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setVisibleCount(pageSize);
    }
  }, [isOpen, pageSize]);

  useEffect(() => {
    if (!isOpen || disabled) return undefined;

    if (portal) {
      updateMenuPosition();
      window.addEventListener("resize", updateMenuPosition);
      window.addEventListener("scroll", updateMenuPosition, true);
    }

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [disabled, isOpen, portal, updateMenuPosition]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const handleScroll = (event) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;

    if (scrollHeight - scrollTop - clientHeight < 24) {
      setVisibleCount((current) => Math.min(current + pageSize, options.length));
    }
  };

  const handleSelect = (nextValue) => {
    if (multiple) {
      onChange(nextValue === "" ? [] : selectedValues.includes(nextValue)
        ? selectedValues.filter((id) => id !== nextValue)
        : [...selectedValues, nextValue]);
      return;
    }
    onChange(nextValue);
    setIsOpen(false);
  };

  const menuContent =
    isOpen && !disabled
      ? (
          <div
            ref={menuRef}
            data-paginated-select-menu
            className={cn(
              portal
                ? "pointer-events-auto fixed z-[1700] overscroll-contain overflow-y-auto rounded-md border border-slate-100 bg-white py-1 shadow-[0_14px_32px_rgba(15,23,42,0.14)]"
                : "absolute left-0 right-0 top-full z-[1700] mt-1 max-h-60 overscroll-contain overflow-y-auto rounded-md border border-slate-100 bg-white py-1 shadow-[0_14px_32px_rgba(15,23,42,0.14)]",
              menuClassName
            )}
            style={portal ? menuStyle : undefined}
            onWheelCapture={stopMenuScrollPropagation}
            onTouchMoveCapture={stopMenuScrollPropagation}
            onScroll={handleScroll}
          >
            <button
              type="button"
              className="block w-full px-3 py-2 text-left text-sm text-slate-500 hover:bg-sky-50 hover:text-sky-700"
              onClick={() => handleSelect("")}
            >
              {multiple ? "Clear selection" : placeholder}
            </button>
            {visibleOptions.map((option) => (
              <button
                key={getOptionKey(option)}
                type="button"
                aria-pressed={multiple ? selectedValues.includes(String(option.value)) : undefined}
                className={cn(
                  "block w-full px-3 py-2 text-left text-sm hover:bg-sky-50 hover:text-sky-700",
                  (multiple ? selectedValues.includes(String(option.value)) : String(option.value) === String(value))
                    ? "bg-sky-50 font-semibold text-sky-700"
                    : "text-slate-700"
                )}
                onClick={() => handleSelect(String(option.value))}
              >
                <span className="block truncate">{option.label}</span>
              </button>
            ))}
            {visibleCount < options.length && (
              <div className="px-3 py-2 text-center text-xs font-medium text-slate-400">
                Scroll for more
              </div>
            )}
            {options.length === 0 && (
              <div className="px-3 py-3 text-center text-sm text-slate-400">
                No options found
              </div>
            )}
          </div>
        )
      : null;
  const menu =
    portal && menuContent
      ? menuStyle
        ? createPortal(menuContent, document.body)
        : null
      : menuContent;

  return (
    <div ref={rootRef} className={cn("relative w-full", className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((current) => !current)}
        className={cn(
          "flex h-10 w-full items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50",
          buttonClassName
        )}
      >
        {Icon && <Icon className="h-4 w-4 shrink-0 text-slate-400" />}
        <span
          className={cn(
            "min-w-0 flex-1 truncate",
            !selectedLabel && "text-slate-400"
          )}
        >
          {selectedLabel || placeholder}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
      </button>
      {menu}
    </div>
  );
};

export default PaginatedSelect;
